import { translate, LocalizedError, userError } from '../i18n/messages';
import type {
  ApplicationCredentialInput,
  ApplicationInput,
  ApplicationRecord,
  ApplicationWebhookInput,
  PriceInput,
  PriceRecord,
  ProductInput,
  ProductRecord,
  ProviderProductMappingInput,
} from './api';

export interface ApplicationFormState {
  id: string | null;
  key: string;
  display_name: string;
  description: string;
  status: string;
  default_environment: string;
  domainsText: string;
}

export interface ProductFormState {
  id: string | null;
  application_id: string;
  key: string;
  display_name: string;
  description: string;
  product_type: string;
  status: string;
  visibility: string;
  is_featured: boolean;
  metadataText: string;
  providerMappingsText: string;
}

export interface PriceFormState {
  id: string | null;
  billing_product_id: string;
  key: string;
  display_name: string;
  description: string;
  currency: string;
  amount: string;
  billing_interval: string;
  trial_days: string;
  is_default: boolean;
  compare_at_amount: string;
  display_badge: string;
  is_recommended: boolean;
  sort_order: string;
  status: string;
  metadataText: string;
  providerMappingsText: string;
}

export interface WebhookFormState {
  id: string | null;
  environment: string;
  url: string;
  subscribedEventTypesText: string;
  status: string;
  signingSecret: string;
}

export interface CredentialFormState {
  id: string | null;
  environment: string;
  display_name: string;
  allowedScopesText: string;
  status: string;
}

export const emptyApplicationForm: ApplicationFormState = {
  id: null,
  key: '',
  display_name: '',
  description: '',
  status: 'active',
  default_environment: '',
  domainsText: '',
};

export const emptyProductForm: ProductFormState = {
  id: null,
  application_id: '',
  key: '',
  display_name: '',
  description: '',
  product_type: 'subscription',
  status: 'active',
  visibility: 'public',
  is_featured: false,
  metadataText: '{}',
  providerMappingsText: '',
};

export const emptyPriceForm: PriceFormState = {
  id: null,
  billing_product_id: '',
  key: '',
  display_name: '',
  description: '',
  currency: 'USD',
  amount: '0',
  billing_interval: 'month',
  trial_days: '',
  is_default: false,
  compare_at_amount: '',
  display_badge: '',
  is_recommended: false,
  sort_order: '0',
  status: 'active',
  metadataText: '{}',
  providerMappingsText: '',
};

export const emptyWebhookForm: WebhookFormState = {
  id: null,
  environment: 'prod',
  url: '',
  subscribedEventTypesText: '',
  status: 'active',
  signingSecret: '',
};

export const emptyCredentialForm: CredentialFormState = {
  id: null,
  environment: 'prod',
  display_name: '',
  allowedScopesText: '',
  status: 'active',
};

export function describeError(error: unknown) {
  return userError(error);
}

export function parseList(text: string) {
  return text
    .split('\n')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export function parseObject(text: string, field: string) {
  try {
    const parsed = JSON.parse(text || '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new LocalizedError('billing.validation.object', { field });
    }
    return parsed as Record<string, unknown>;
  } catch (error) {
    if (error instanceof LocalizedError) throw error;
    throw new LocalizedError('billing.validation.json', { field });
  }
}

export function parseDomains(text: string): ApplicationInput['domains'] {
  return parseList(text).map((entry) => {
    const [domain, environment, primaryFlag] = entry.split('|');
    if (!domain || !environment) {
      throw new LocalizedError(
        'billing.ui.domains.must.use.domain.environment.primary.format.on.each.line',
      );
    }
    return {
      domain,
      environment,
      is_primary: primaryFlag === 'true',
    };
  });
}

export function formatDomains(application: ApplicationRecord) {
  return application.domains
    .map((entry) => `${entry.domain}|${entry.environment}|${String(entry.is_primary)}`)
    .join('\n');
}

export function parseProviderMappings(text: string): ProviderProductMappingInput[] {
  return parseList(text).map((entry) => {
    const [
      providerKey,
      environment,
      status,
      providerProductId = '',
      providerPriceId = '',
      metadataText = '{}',
    ] = entry.split('|');
    if (!providerKey || !environment || !status) {
      throw new LocalizedError(
        'billing.ui.provider.mappings.must.use.provider.key.environment.status.provider.product.id.provider.price.id.metadatajson.format',
      );
    }
    return {
      provider_key: providerKey,
      environment,
      status,
      provider_product_id: providerProductId || null,
      provider_price_id: providerPriceId || null,
      provider_metadata: parseObject(
        metadataText,
        translate('billing.ui.provider.mapping.metadata'),
      ),
    };
  });
}

export function formatProviderMappings(product: ProductRecord) {
  return product.provider_mappings
    .map((entry) =>
      [
        entry.provider_key,
        entry.environment,
        entry.status,
        entry.provider_product_id ?? '',
        entry.provider_price_id ?? '',
        JSON.stringify(entry.provider_metadata ?? {}),
      ].join('|'),
    )
    .join('\n');
}

export function formatPriceProviderMappings(price: PriceRecord) {
  return price.provider_mappings
    .map((entry) =>
      [
        entry.provider_key,
        entry.environment,
        entry.status,
        entry.provider_product_id ?? '',
        entry.provider_price_id ?? '',
        JSON.stringify(entry.provider_metadata ?? {}),
      ].join('|'),
    )
    .join('\n');
}

export function formatMetadata(product: ProductRecord) {
  return JSON.stringify(product.metadata ?? {}, null, 2);
}

export function formatPriceMetadata(price: PriceRecord) {
  return JSON.stringify(price.metadata ?? {}, null, 2);
}

function parseInteger(value: string, field: string) {
  const parsed = Number(value);
  if (!Number.isInteger(parsed)) {
    throw new LocalizedError('billing.validation.integer', { field });
  }
  return parsed;
}

function parseOptionalInteger(value: string, field: string) {
  const trimmed = value.trim();
  return trimmed ? parseInteger(trimmed, field) : null;
}

export function formatPrice(price: PriceRecord): PriceFormState {
  return {
    id: price.id,
    billing_product_id: price.billing_product_id,
    key: price.key,
    display_name: price.display_name,
    description: price.description ?? '',
    currency: price.currency,
    amount: String(price.amount),
    billing_interval: price.billing_interval,
    trial_days: price.trial_days === null ? '' : String(price.trial_days),
    is_default: price.is_default,
    compare_at_amount: price.compare_at_amount === null ? '' : String(price.compare_at_amount),
    display_badge: price.display_badge ?? '',
    is_recommended: price.is_recommended,
    sort_order: String(price.sort_order),
    status: price.status,
    metadataText: formatPriceMetadata(price),
    providerMappingsText: formatPriceProviderMappings(price),
  };
}

export function parseWebhookInput(form: WebhookFormState): ApplicationWebhookInput {
  return {
    environment: form.environment,
    url: form.url,
    subscribed_event_types: parseList(form.subscribedEventTypesText),
    status: form.status,
    signing_secret: form.signingSecret ? form.signingSecret : undefined,
  };
}

export function formatWebhook(application: ApplicationRecord, webhookId: string) {
  const webhook = application.webhook_endpoints.find((entry) => entry.id === webhookId);
  if (!webhook) {
    return emptyWebhookForm;
  }
  return {
    id: webhook.id,
    environment: webhook.environment,
    url: webhook.url,
    subscribedEventTypesText: webhook.subscribed_event_types.join('\n'),
    status: webhook.status,
    signingSecret: '',
  };
}

export function formatCredential(application: ApplicationRecord, credentialId: string) {
  const credential = application.service_clients.find((entry) => entry.id === credentialId);
  if (!credential) {
    return emptyCredentialForm;
  }
  return {
    id: credential.id,
    environment: credential.environment,
    display_name: credential.display_name,
    allowedScopesText: credential.allowed_scopes.join('\n'),
    status: credential.status,
  };
}

export function toApplicationInput(form: ApplicationFormState): ApplicationInput {
  return {
    key: form.key,
    display_name: form.display_name,
    description: form.description || null,
    status: form.status,
    default_environment: form.default_environment || null,
    domains: parseDomains(form.domainsText),
  };
}

export function toProductInput(form: ProductFormState): ProductInput {
  return {
    application_id: form.application_id,
    key: form.key,
    display_name: form.display_name,
    description: form.description || null,
    product_type: form.product_type,
    is_featured: form.is_featured,
    visibility: (form.visibility as ProductInput['visibility']) || 'public',
    status: form.status,
    metadata: parseObject(form.metadataText, translate('billing.ui.product.metadata')),
    provider_mappings: parseProviderMappings(form.providerMappingsText),
  };
}

export function toPriceInput(form: PriceFormState): PriceInput {
  return {
    billing_product_id: form.billing_product_id,
    key: form.key,
    display_name: form.display_name,
    description: form.description || null,
    currency: form.currency,
    amount: parseInteger(form.amount, translate('billing.column.amount')),
    billing_interval: form.billing_interval,
    trial_days: parseOptionalInteger(form.trial_days, translate('billing.ui.trial.days')),
    is_default: form.is_default,
    compare_at_amount: parseOptionalInteger(
      form.compare_at_amount,
      translate('billing.ui.compare.at.amount'),
    ),
    display_badge: form.display_badge || null,
    is_recommended: form.is_recommended,
    sort_order: parseInteger(form.sort_order, translate('billing.ui.sort.order')),
    status: form.status,
    metadata: parseObject(form.metadataText, translate('billing.ui.price.metadata')),
    provider_mappings: parseProviderMappings(form.providerMappingsText),
  };
}

export function toCredentialInput(form: CredentialFormState): ApplicationCredentialInput {
  return {
    environment: form.environment,
    display_name: form.display_name,
    allowed_scopes: parseList(form.allowedScopesText),
    status: form.status,
  };
}
